import React, { useEffect, useState } from 'react';
import DoctorPicker from '../../procedures/DoctorPicker';
import { DOCTORS_LOAD_ERROR_COPY } from '../../../hooks/useAssignableProviders';
import { fetchDoctors } from '../../../services/api/visitApi';
import { STAFF_STATUS } from '../../../constants/staff';
import { PRACTITIONER } from '../../../constants/hipRecordsCopy';

// The server's own rule (PatientService._validate_doctor via records.service.resolve_practitioner):
// a member with no status counts as ACTIVE; any other status (INVITED, SUSPENDED...) is refused.
const isActive = (d) => (d.status || STAFF_STATUS.ACTIVE).toUpperCase() === STAFF_STATUS.ACTIVE;

/**
 * HipPractitionerPicker - the required "Doctor for patient" field (OR-59, label OR-64). Shown only where a Link is
 * made for a visit with no doctor (`EncounterRow.needsPractitioner`): the Link / Link all confirm
 * on the card, the inline Add file panel (OR-82) and the Doctor row of the Invoice drawer (OR-106).
 *
 * The list is the ORGANISATION's active doctors (OR-59; §4.7 A3): visit registration's org-wide doctors
 * source, fetchDoctors (GET /api/organisation/mem?role=DOCTOR), kept to ACTIVE members - the same set
 * the server accepts. NOT the department-walled /providers/assignable list: the server validates the
 * pick org-wide, so a walled viewer must still see every doctor. Drawn by the shared searchable
 * DoctorPicker. Fetched once when this component mounts (once per open of a confirm / drawer).
 *
 * No active doctor in the organisation: says how to fix it (the server's NO_DOCTORS_IN_ORGANISATION
 * message) in place of an empty picker; nothing can be picked, so the caller's Confirm stays disabled.
 *
 * Props: { value: member_id | '', onChange(memberId | ''), showReason = true, showLabel = true }
 *   showReason: the "Pick the doctor..." line under the picker while nothing is picked. The inline Add file
 *   panel turns it off because it shows the same reason beside its disabled button.
 *   showLabel: the visible "Doctor for patient" title (the group keeps it as its aria-label). The inline Add file
 *   panel turns it off because it draws that title itself.
 */
export default function HipPractitionerPicker({ value, onChange, showReason = true, showLabel = true }) {
  const [doctors, setDoctors] = useState(null); // null = loading
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchDoctors()
      .then(({ doctors: list }) => { if (!cancelled) setDoctors((list || []).filter(isActive)); })
      .catch(() => { if (!cancelled) { setDoctors([]); setError(true); } });
    return () => { cancelled = true; };
  }, []);

  const none = doctors !== null && !error && doctors.length === 0;

  return (
    <div role="group" aria-label={PRACTITIONER.label} data-testid="hip-practitioner-picker" className="mb-5 text-left">
      {showLabel && <div className="mb-1.5 text-xs font-bold text-slate-800 dark:text-slate-100">{PRACTITIONER.label}</div>}
      {none ? (
        <p role="alert" className="m-0 text-xs font-semibold text-amber-800 dark:text-amber-300">{PRACTITIONER.noDoctors}</p>
      ) : (
        <>
          <DoctorPicker
            doctors={doctors || []}
            selectedId={value || null}
            onChange={(id) => onChange(id || '')}
            isLoading={doctors === null}
            showSubline={false}
            clearLabel="✕ Clear selection"
          />
          <p className="m-0 mt-1 text-xs text-slate-600 dark:text-slate-300">{PRACTITIONER.hint}</p>
          {error && <p role="alert" className="m-0 mt-1 text-xs font-semibold text-red-600 dark:text-red-400">{DOCTORS_LOAD_ERROR_COPY}</p>}
          {showReason && !value && (
            <p className="m-0 mt-1 text-xs font-semibold text-slate-700 dark:text-slate-300">{PRACTITIONER.pickReason}</p>
          )}
        </>
      )}
    </div>
  );
}
